import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Orders from "./pages/Orders";
import OrderSuccess from "./pages/OrderSuccess";
import Wishlist from "./pages/Wishlist";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import AuthCallback from "./pages/AuthCallback";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ShippingReturns from "./pages/ShippingReturns";
import Terms from "./pages/Terms";
import CareGuide from "./pages/CareGuide";
import TrackOrder from "./pages/TrackOrder";
import NotFound from "./pages/NotFound";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

const GUEST_CART_KEY = "sumanya_guest_cart";
const readGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};
const writeGuestCart = (items) => {
  try { localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items || [])); } catch { /* ignore quota */ }
};

function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [wishlist, setWishlist] = useState([]);

  const refreshCart = useCallback(async () => {
    if (user) {
      try {
        const r = await axios.get(`${API}/cart`);
        setCart(r.data);
      } catch { /* noop */ }
      return;
    }
    // Guest cart — hydrate from localStorage, then enrich prices via public endpoint
    const raw = readGuestCart();
    if (!raw.length) { setCart({ items: [], total: 0, is_guest: true }); return; }
    try {
      const r = await axios.post(`${API}/cart/enrich`, { items: raw });
      // Prune any items that no longer exist
      const availableIds = new Set(r.data.items.map(i => i.product_id));
      const pruned = raw.filter(i => availableIds.has(i.product_id));
      if (pruned.length !== raw.length) writeGuestCart(pruned);
      setCart({ ...r.data, is_guest: true });
    } catch {
      setCart({ items: [], total: 0, is_guest: true });
    }
  }, [user]);

  // Guest-cart mutators (used by product pages, cart page)
  const addToGuestCart = useCallback(async ({ product_id, quantity = 1, size = null }) => {
    const items = readGuestCart();
    const idx = items.findIndex(i => i.product_id === product_id && (i.size || null) === (size || null));
    if (idx >= 0) items[idx].quantity += quantity;
    else items.push({ product_id, quantity, size });
    writeGuestCart(items);
    await refreshCart();
  }, [refreshCart]);

  const updateGuestCartItem = useCallback(async ({ product_id, size = null, quantity }) => {
    let items = readGuestCart();
    if (quantity <= 0) {
      items = items.filter(i => !(i.product_id === product_id && (i.size || null) === (size || null)));
    } else {
      const idx = items.findIndex(i => i.product_id === product_id && (i.size || null) === (size || null));
      if (idx >= 0) items[idx].quantity = quantity;
    }
    writeGuestCart(items);
    await refreshCart();
  }, [refreshCart]);

  const clearGuestCart = useCallback(() => {
    writeGuestCart([]);
    setCart({ items: [], total: 0, is_guest: true });
  }, []);

  const refreshWishlist = useCallback(async () => {
    if (!user) { setWishlist([]); return; }
    try {
      const r = await axios.get(`${API}/wishlist`);
      setWishlist(r.data.products || []);
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => { refreshCart(); refreshWishlist(); }, [refreshCart, refreshWishlist]);

  return (
    <CartContext.Provider value={{
      cart, refreshCart,
      wishlist, refreshWishlist,
      addToGuestCart, updateGuestCartItem, clearGuestCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

function AppShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-[60vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:category" element={<Products />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/order-success/:id" element={<OrderSuccess />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/shipping-returns" element={<ShippingReturns />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/care-guide" element={<CareGuide />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <Toaster position="top-right" richColors style={{ zIndex: 99999 }} />
            <AppShell />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
